import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Table } from "antd"
import { Button, Loader } from "../../components"
import { previewCycleCount } from "../../services/cycleCountServices"

const T_PREFIX = 'cycle_counts.bulk_assign.confirm_step'

export const BulkAssignConfirmStep = ({
    warehouseId,
    filters,
    selectedOperators,
    onConfirm,
    onBack,
    busy,
}) => {
    const { i18n } = useTranslation()
    const [distribution, setDistribution] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [retryCount, setRetryCount] = useState(0)

    useEffect(() => {
        let cancelled = false

        const cleanFilters = {}
        if (filters.area?.length) cleanFilters.area = filters.area
        if (filters.row?.length) cleanFilters.row = filters.row
        if (filters.store_id?.length) cleanFilters.store_id = filters.store_id

        setLoading(true)
        setError(null)

        previewCycleCount({
            warehouse_id: parseInt(warehouseId),
            filters: cleanFilters,
            assigned_to_ids: selectedOperators.map(op => op.id),
        })
            .then((data) => {
                if (!cancelled) {
                    setDistribution(data.distribution)
                    setLoading(false)
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(i18n.t(`${T_PREFIX}.error`))
                    setLoading(false)
                }
            })

        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseId, retryCount])

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center py-12">
                <Loader show={true} />
                <p className="text-sm text-gray-500 mt-2">{i18n.t(`${T_PREFIX}.loading`)}</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-500 mb-4">{error}</p>
                <div className="flex justify-center gap-3">
                    <Button type="secondary" onClick={onBack}>
                        {i18n.t(`${T_PREFIX}.back`)}
                    </Button>
                    <Button type="primary" onClick={() => setRetryCount(c => c + 1)}>
                        {i18n.t(`${T_PREFIX}.retry`)}
                    </Button>
                </div>
            </div>
        )
    }

    const columns = [
        {
            title: i18n.t(`${T_PREFIX}.operator_column`),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: i18n.t(`${T_PREFIX}.count_column`),
            dataIndex: 'count',
            key: 'count',
            align: 'right',
        },
        {
            title: i18n.t(`${T_PREFIX}.from_column`),
            dataIndex: 'from',
            key: 'from',
            render: (val) => <span className="font-mono text-sm">{val}</span>
        },
        {
            title: i18n.t(`${T_PREFIX}.to_column`),
            dataIndex: 'to',
            key: 'to',
            render: (val) => <span className="font-mono text-sm">{val}</span>
        },
    ]

    return (
        <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                {i18n.t(`${T_PREFIX}.title`)}
            </h3>

            <Table
                size="small"
                columns={columns}
                dataSource={(distribution || []).map((d, idx) => ({ ...d, key: idx }))}
                pagination={false}
            />

            <div className="mt-4 flex justify-between">
                <Button type="secondary" onClick={onBack}>
                    {i18n.t(`${T_PREFIX}.back`)}
                </Button>
                <Button
                    type="primary"
                    disabled={busy || !distribution}
                    onClick={onConfirm}
                >
                    {i18n.t(`${T_PREFIX}.confirm_button`)}
                </Button>
            </div>
        </div>
    )
}
